import "./extends";
import { Application } from 'pixi.js';
import { SceneManager, SceneConstructor } from './SceneManager/SceneManager';
import { TimeEvents } from './TimeEvents/TimeEvents';
import { SoundManager } from './SoundManager/SoundManager';
import { TweenManager } from './TweenManager/TweenManager';
import { Loader } from './Loader/Loader';
import { Signal } from 'framework/utils/Signal';
import { default as languages } from 'json/languages.json';
import { ScenesName } from "js/scenes/scenes";
import { loadUserData } from "js/sdk/loadUserData";
// import { ILeaderboardHandler } from "@softgames/firebase-leaderboard/dist/types";
import initialize from "js/sdk/initialize";
// import initLeaderboad from "@softgames/firebase-leaderboard";
import OS from "./utils/OS";
import { showInterstitial } from '../sdk/showInterstitial';
import { ISgSdkHandler } from "js/sdk/types";
declare const sdkHandler: ISgSdkHandler;

interface IGameConfig {
  resizeHandler: () => [number, number];
  scenes: {[key: string]: SceneConstructor};
}

export class Game extends Application {
  private readonly config: IGameConfig;
  private locale: string = 'en';

  private paused: boolean = false;

  public signals = {
    onResize: new Signal<[number, number]>(),
  };
  public readonly scenes: SceneManager;
  public readonly timeEvents: TimeEvents;
  public readonly tweens: TweenManager;
  public readonly sounds: SoundManager;
  // public leaderboard: ILeaderboardHandler;

  constructor(pixiOptions: PIXI.ApplicationOptions, gameConfig: IGameConfig) {
    super(pixiOptions);

    // [WEB-1958] Destroy the default plugin AccessibilityManager because the accessibility
    // layer is blocking input for first time users, after consent popup, on mobile devices.
    this.renderer.plugins.accessibility.destroy();

    this.config = gameConfig;

    this.timeEvents = new TimeEvents();
    this.tweens = new TweenManager();
    this.scenes = new SceneManager(this, this.config.scenes);
    this.sounds = new SoundManager();
    this.ticker.add(this.update);
    window.addEventListener('resize', this.handleResize);
    (document.getElementById('content') || document.body).appendChild(this.view);
  }

  public initialize() {
    return initialize({
      supportedLanguages: Object.keys(languages),
      runGame: async () => {
        sdkHandler.isAdPLaying = true;
        this.scenes.startScene(ScenesName.Main);
        if (window._YaSDK) {
          window._YaSDK.features.LoadingAPI?.ready();
        }
        await showInterstitial();
      },
      freezeGame() {
        throw "Not implemented";
      },
      unfreezeGame() {
        throw "Not implemented";
      },
      startOver() {
        throw "Not implemented";
      },
      getScore() {
        throw "Not implemented";
      },
    })
      .then(async ({config, handler}) => {
        this.setLocale(config.env.locale);

        // this.leaderboard = await initLeaderboad({
        //   firebaseConfig,
        //   publisherId: `${config.publisher.id}`,
        // });

        return { config, handler };
      });
  }

  private setLocale(lang: string): void {
    if (Object.keys(languages).includes(lang)) {
      this.locale = lang;
    } else {
      this.locale = 'en';
    }
  }

  public togglePause(): void {
    this.paused = !this.paused;

    if(this.paused) {
      this.ticker.stop();
    } else {
      this.ticker.start();
    }

    this.render();
  }

  public isPaused(): boolean {
    return this.paused;
  }

  public getLocalized(text: string): string {
    try {
      if (languages[this.locale][text]) {
        return languages[this.locale][text];
      }
      return `${text}***`;
    } catch (e) {
      console.warn(e);
      return `${text}***`;
    }
  }

  public async load() {
    this.stage.visible = false;
    await new Loader().load();
    this.handleResize();
    this.stage.visible = true;
    this.sounds.init((await loadUserData(['soundEnabled'])).soundEnabled ?? true);
    window.h5branding.SplashLoader.getInstance().setLoadProgress(100);
  }

  private handleResize = () => {
    const [width, height] = this.config.resizeHandler();
    this.renderer.resize(Math.ceil(width), Math.ceil(height));
    this.view.style.width = window.innerWidth + 'px';
    this.view.style.height = window.innerHeight +'px';
    this.scenes.handleResize([this.screen.width, this.screen.height]);
    this.signals.onResize.dispatch([this.screen.width, this.screen.height]);

    this.ticker.update();
  }

  private update = () => {
    this.timeEvents.update(this.ticker.elapsedMS);
    this.tweens.update(this.ticker.elapsedMS);
    this.scenes.update(this.ticker.elapsedMS);
    this.sounds.update();
  }
}
